<!-- eslint-disable camelcase -->
<script setup lang="ts">
import { nextTick, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ElForm, ElInput } from 'element-plus';
import { hex_sha1 } from '@zeronejs/utils';
import { postV1ChatloginChatLogin } from '@/api/gassApi/controller';
import { setToken } from '@/utils/auth';
import { useAppStore } from '@/stores/app';
const router = useRouter();
const route = useRoute();
const appStore = useAppStore();

const loginRules = reactive({
  username: [{ required: true, trigger: 'blur' }],
  password: [{ required: true, trigger: 'blur' }],
});
const loginForm = reactive({
  username: '',
  password: '',
});
const loading = ref(false);
const passwordType = ref('password');
const redirect = ref();
const passwordRef = ref<InstanceType<typeof ElInput>>();
const showPwd = () => {
  if (passwordType.value === 'password') {
    passwordType.value = '';
  } else {
    passwordType.value = 'password';
  }
  nextTick(() => {
    passwordRef.value?.focus();
  });
};
const loginFormRef = ref<InstanceType<typeof ElForm>>();
const handleLogin = () => {
  loginFormRef.value?.validate(async valid => {
    if (valid) {
      loading.value = true;
      try {
        const { data } = await postV1ChatloginChatLogin({
          user_name: loginForm.username,
          pwd: hex_sha1(loginForm.password),
        });
        loading.value = false;
        if (data.code !== 0) {
          return;
        }
        setToken(data.data);
        await appStore.triggerLoginResult({ token: data.data, userId: '' });
        appStore.showLogin = false;
      } catch {
        loading.value = false;
        return;
      }
      loading.value = false;
    } else {
      console.log('error submit!!');
      return false;
    }
  });
};
watch(
  () => route.query,
  query => {
    redirect.value = query && query.redirect;
  },
  {
    immediate: true,
  },
);
</script>
<template>
  <div class="login-container">
    <el-form ref="loginFormRef" :model="loginForm" :rules="loginRules" class="login-form" auto-complete="on" label-position="left">
      <div class="title-container">
        <h3 class="title">登录</h3>
      </div>

      <el-form-item prop="username" class="mb-3">
        <span class="svg-container">
          <svg-icon icon-class="user" />
        </span>
        <el-input ref="username" v-model="loginForm.username" name="username" type="text" tabindex="1" auto-complete="on" placeholder="用户名" />
      </el-form-item>

      <el-form-item prop="password" class="relative mb-3">
        <span class="svg-container">
          <svg-icon icon-class="password" />
        </span>
        <el-input
          :key="passwordType"
          ref="passwordRef"
          v-model="loginForm.password"
          :type="passwordType"
          placeholder="密码"
          name="password"
          tabindex="2"
          auto-complete="on"
          @keyup.enter="handleLogin"
        />
        <span class="show-pwd" @click="showPwd">
          <svg-icon :icon-class="passwordType === 'password' ? 'eye' : 'eye-open'" />
        </span>
      </el-form-item>

      <el-button :loading="loading" type="primary" style="width: 100%; margin-bottom: 30px" @click.prevent="handleLogin"> 登录 </el-button>

      <!-- <div class="tips">
                <span style="margin-right: 20px">username: admin</span>
                <span> password: any</span>
            </div> -->
    </el-form>
  </div>
</template>

<style lang="scss">
/* 修复input 背景不协调 和光标变色 */
/* Detail see https://github.com/PanJiaChen/vue-element-admin/pull/927 */

$bg: #283443;
$light_gray: #fff;
$cursor: #fff;

@supports (-webkit-mask: none) and (not (cater-color: $cursor)) {
  .login-container .el-input input {
    color: $cursor;
  }
}

/* reset element-ui css */
.login-container {
  .el-input {
    display: inline-block;
    height: 49px;
    width: 85%;
    .el-input__wrapper {
      width: 100%;
      background: $bg;
      box-shadow: none;
      border: none;
      height: 49px;
      input {
        width: 100%;
        background: $bg;
        border: 0px;
        -webkit-appearance: none;
        border-radius: 0px;
        padding: 12px 5px 12px 15px;
        color: $light_gray;
        height: 47px;
        caret-color: $cursor;

        &:-webkit-autofill {
          box-shadow: 0 0 0px 1000px $bg inset !important;
          -webkit-text-fill-color: $cursor !important;
        }
      }
    }
  }

  .el-form-item {
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.1);
    border-radius: 5px;
    color: #454545;
  }
}
</style>

<style lang="scss" scoped>
$bg: #2d3a4b;
$dark_gray: #889aa4;
$light_gray: #eee;

.login-container {
  min-height: 100%;
  width: 100%;
  background-color: $bg;
  overflow: hidden;

  .login-form {
    position: relative;
    width: 520px;
    max-width: 100%;
    padding: 160px 35px 0;
    margin: 0 auto;
    overflow: hidden;
  }

  .tips {
    font-size: 14px;
    color: #fff;
    margin-bottom: 10px;

    span {
      &:first-of-type {
        margin-right: 16px;
      }
    }
  }

  .svg-container {
    padding: 6px 5px 6px 15px;
    color: $dark_gray;
    vertical-align: middle;
    width: 30px;
    display: inline-block;
  }

  .title-container {
    position: relative;

    .title {
      font-size: 26px;
      color: $light_gray;
      margin: 0px auto 40px auto;
      text-align: center;
      font-weight: bold;
    }
  }

  .show-pwd {
    position: absolute;
    right: 10px;
    top: 16px;
    font-size: 16px;
    color: $dark_gray;
    cursor: pointer;
    user-select: none;
  }
}
</style>
